import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Switch, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Check, DeleteOutline, Refresh, RemoveCircleOutline } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import StudioSelector, { StudioOption } from '../components/StudioSelector';
import { STUDIO_SCOPED_ROLES, isOwnerRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchSelect from '../components/BranchSelect';

/**
 * Milestones — visit badges, each optionally carrying a reward.
 *
 * Same tenancy as Referrals: a studio user never sends a studio id (the
 * backend reads it from the token); a super admin names one via the picker.
 *
 * A rung's reward is free text in the studio's own words ("Grip socks",
 * "10% off your next pack"). Nothing is applied automatically: when a member
 * reaches it, it lands in "To hand over" and staff give it and mark it done.
 */


interface Tier {
  visits: number | '';
  title: string;
  rewardText: string;
}

interface AwardsShape {
  rows: any[];
  total: number;
  page: number;
  pages: number;
  summary: { toHandOver: number; fulfilled: number; revoked: number };
}

/** The hand-over list's filters, in the order staff work through them. */
const AWARD_FILTERS: { value: string; label: string; count?: 'toHandOver' | 'fulfilled' | 'revoked' }[] = [
  { value: 'earned', label: 'To hand over', count: 'toHandOver' },
  { value: 'fulfilled', label: 'Handed over', count: 'fulfilled' },
  { value: 'revoked', label: 'Revoked', count: 'revoked' },
  { value: '', label: 'All' },
];

/** One tap for the usual reasons; "Other" opens a text box. */
const REVOKE_REASONS = ['Checked in by mistake', 'Duplicate entry', 'Not eligible'];

const shortDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

const initials = (name?: string) =>
  (name ?? '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

const toTier = (t: any): Tier => ({
  visits: t.visits ?? '',
  title: t.title ?? '',
  rewardText: t.rewardText ?? '',
});


const SectionCard: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Card sx={{ ...sx }}><CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>{children}</CardContent></Card>
);

const MilestonesPage: React.FC = () => {
  const { admin } = useAuth();
  const role = admin?.role ?? '';
  const isStudioRole = STUDIO_SCOPED_ROLES.includes(role);
  const isSuperAdmin = role === 'super_admin' || role === 'admin';
  const canActivate = isOwnerRole(role) || isSuperAdmin;

  const enabled = (admin?.studio as any)?.studioPermissions?.enableMilestones === true;
  const blocked = isStudioRole && !enabled;

  const [selectedStudio, setSelectedStudio] = useState<StudioOption | null>(null);
  const studioParam = isSuperAdmin ? selectedStudio?._id : undefined;
  const { isAllLocations } = useMyLocations();
  const [branch, setBranch] = useState('');
  const pickBranch = isStudioRole && isAllLocations;
  const pinStudioId = pickBranch ? branch || undefined : undefined;
  const needsStudio = (isSuperAdmin && !selectedStudio) || (pickBranch && !branch);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [tiers, setTiers] = useState<Tier[]>([]);
  const [terms, setTerms] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [programExists, setProgramExists] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [awards, setAwards] = useState<AwardsShape | null>(null);
  const [awardStatus, setAwardStatus] = useState('earned');
  const [awardPage, setAwardPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<any | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeOther, setRevokeOther] = useState('');

  const err = (e: any, fallback: string) =>
    setError(e?.response?.data?.message || e?.message || fallback);

  // Awards load on their own so paging or filtering never discards unsaved
  // milestone edits.
  const loadAwards = useCallback(async () => {
    if (blocked || needsStudio) return;
    try {
      const a = await apiService.getMilestoneAwards({
        ...(studioParam ? { studioId: studioParam } : {}),
        ...(awardStatus ? { status: awardStatus } : {}),
        page: awardPage,
        limit: 25,
      }, pinStudioId);
      setAwards(a ?? null);
    } catch (e: any) {
      err(e, 'Could not load rewards');
    }
  }, [blocked, needsStudio, studioParam, pinStudioId, awardStatus, awardPage]);

  const loadProgram = useCallback(async () => {
    if (blocked || needsStudio) return;
    setLoading(true);
    setError(null);
    try {
      const p = await apiService.getMilestoneProgram(studioParam, pinStudioId);
      const src = p?.program ?? p?.defaults ?? { tiers: [] };
      setTiers((src.tiers ?? []).map(toTier));
      setTerms(p?.program?.terms ?? '');
      setIsActive(!!p?.program?.isActive);
      setProgramExists(!!p?.program);
      setDirty(false);
    } catch (e: any) {
      err(e, 'Could not load milestones');
    } finally {
      setLoading(false);
    }
  }, [blocked, needsStudio, studioParam, pinStudioId]);

  const load = useCallback(async () => {
    await Promise.all([loadProgram(), loadAwards()]);
  }, [loadProgram, loadAwards]);

  useEffect(() => { loadProgram(); }, [loadProgram]);
  useEffect(() => { loadAwards(); }, [loadAwards]);

  const patchTier = (i: number, patch: Partial<Tier>) => {
    setTiers((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
    setDirty(true);
  };

  const addTier = () => {
    const last = tiers.reduce((m, t) => Math.max(m, Number(t.visits) || 0), 0);
    setTiers((ts) => [...ts, { visits: last ? last * 2 : 1, title: '', rewardText: '' }]);
    setDirty(true);
  };

  const removeTier = (i: number) => {
    setTiers((ts) => ts.filter((_, j) => j !== i));
    setDirty(true);
  };

  /** First problem found, or null. Mirrors the backend's checks. */
  const invalid = useMemo(() => {
    const seen = new Set<number>();
    for (const t of tiers) {
      const v = Number(t.visits);
      if (!Number.isInteger(v) || v < 1) return 'Every milestone needs a visit count of 1 or more.';
      if (seen.has(v)) return `Two milestones are set at ${v} visits.`;
      seen.add(v);
    }
    return null;
  }, [tiers]);

  const save = async () => {
    if (invalid) { setError(invalid); return; }
    setSaving(true);
    setError(null);
    try {
      await apiService.upsertMilestoneProgram({
        tiers: tiers.map((t) => ({
          visits: Number(t.visits),
          title: t.title.trim() || null,
          rewardText: t.rewardText.trim() || null,
        })),
        terms: terms.trim() || null,
      }, studioParam, pinStudioId);
      setNotice(programExists ? 'Saved.' : 'Saved — paused until you switch it on.');
      await load();
    } catch (e: any) {
      err(e, 'Could not save milestones');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiService.setMilestoneProgramActive(next, studioParam, pinStudioId);
      setIsActive(next);
      const skipped = res?.baselined ?? 0;
      setNotice(next
        ? `Live.${skipped ? ` ${skipped} rewards already passed by existing members were skipped.` : ''}`
        : 'Paused.');
    } catch (e: any) {
      err(e, 'Could not change the state');
    } finally {
      setSaving(false);
    }
  };

  const fulfil = async (a: any) => {
    setBusyId(a._id);
    try {
      await apiService.fulfilMilestoneAward(a._id, studioParam, pinStudioId);
      setNotice(`Handed over to ${a.user?.name ?? 'member'}.`);
      await loadAwards();
    } catch (e: any) {
      err(e, 'Could not update the reward');
    } finally {
      setBusyId(null);
    }
  };

  const openRevoke = (a: any) => {
    setRevokeTarget(a);
    setRevokeReason('');
    setRevokeOther('');
  };

  const finalReason = revokeReason === 'Other' ? revokeOther.trim() : revokeReason;

  const confirmRevoke = async () => {
    if (!revokeTarget || !finalReason) return;
    setBusyId(revokeTarget._id);
    try {
      await apiService.revokeMilestoneAward(revokeTarget._id, finalReason, studioParam, pinStudioId);
      setNotice('Reward revoked.');
      setRevokeTarget(null);
      await loadAwards();
    } catch (e: any) {
      err(e, 'Could not revoke the reward');
    } finally {
      setBusyId(null);
    }
  };

  if (blocked) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Milestones are not enabled for this studio yet. Ask SweatSocial to turn on
          <strong> Enable Milestones</strong>.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        display="flex" justifyContent="space-between" alignItems="flex-start"
        flexWrap="wrap" gap={2} mb={2.5}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: colors.textPrimary }}>
            Milestones
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            A badge at each visit count, with an optional reward.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          {isSuperAdmin && (
            <StudioSelector
              value={selectedStudio} onChange={setSelectedStudio} required minWidth={240}
            />
          )}
          {pickBranch && (
            <Box minWidth={240}>
              <BranchSelect value={branch} onChange={setBranch} minRole="owner" />
            </Box>
          )}
          <Tooltip title="Reload">
            <span>
              <Button variant="outlined" onClick={load} disabled={loading || needsStudio}>
                <Refresh fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {needsStudio ? (
        <Alert severity="info">{pickBranch ? 'Pick a location' : 'Pick a studio'} to see its milestones.</Alert>
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          {/* State */}
          <SectionCard sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Chip
                  size="small"
                  label={isActive ? 'Live' : programExists ? 'Paused' : 'Not set up'}
                  color={isActive ? 'success' : 'default'}
                />
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  {isActive
                    ? 'Members earn rewards as they check in.'
                    : 'Members see badges only. No rewards are given.'}
                </Typography>
              </Box>
              <Tooltip title={
                !programExists ? 'Save first'
                  : !canActivate ? 'Only the owner can switch this'
                  : dirty ? 'Save changes first' : ''
              }>
                <span>
                  <Switch
                    checked={isActive}
                    disabled={!programExists || !canActivate || saving || dirty}
                    onChange={(e) => toggleActive(e.target.checked)}
                  />
                </span>
              </Tooltip>
            </Box>
          </SectionCard>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Milestones" />
            <Tab label={`To hand over${awards?.summary?.toHandOver ? ` (${awards.summary.toHandOver})` : ''}`} />
          </Tabs>

          {tab === 0 && (
            <SectionCard>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={110}>Visits</TableCell>
                      <TableCell width={180}>Badge title</TableCell>
                      <TableCell>Reward</TableCell>
                      <TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tiers.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <TextField
                            size="small" type="number" value={t.visits}
                            onChange={(e) => patchTier(i, { visits: e.target.value === '' ? '' : Number(e.target.value) })}
                            inputProps={{ min: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" fullWidth value={t.title}
                            placeholder={t.visits ? `${t.visits} visits` : ''}
                            onChange={(e) => patchTier(i, { title: e.target.value })}
                            inputProps={{ maxLength: 40 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" fullWidth value={t.rewardText}
                            placeholder="e.g. Grip socks, 10% off next pack"
                            onChange={(e) => patchTier(i, { rewardText: e.target.value })}
                            inputProps={{ maxLength: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove">
                            <IconButton size="small" onClick={() => removeTier(i)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box mt={1.5}>
                <Button startIcon={<Add />} onClick={addTier}>Add milestone</Button>
              </Box>

              <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                <TextField
                  size="small" label="Terms (shown in app)" value={terms} sx={{ flex: 1, minWidth: 280 }}
                  onChange={(e) => { setTerms(e.target.value); setDirty(true); }}
                  multiline maxRows={4}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2.5}>
                {invalid && dirty && (
                  <Typography variant="body2" color="error">{invalid}</Typography>
                )}
                <Button
                  variant="contained" onClick={save}
                  disabled={saving || (!dirty && programExists) || !!invalid}
                >
                  {saving ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              </Box>
            </SectionCard>
          )}

          {tab === 1 && (
            <SectionCard>
              {/* Filters with counts — staff work the first one down to zero. */}
              <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                {AWARD_FILTERS.map((f) => {
                  const on = awardStatus === f.value;
                  const n = f.count ? awards?.summary?.[f.count] ?? 0 : undefined;
                  return (
                    <Chip
                      key={f.label}
                      label={n !== undefined ? `${f.label} · ${n}` : f.label}
                      onClick={() => { setAwardStatus(f.value); setAwardPage(1); }}
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      sx={{ fontWeight: on ? 600 : 400 }}
                    />
                  );
                })}
              </Box>

              <TableContainer>
                <Table size="small" sx={{ '& td, & th': { borderColor: '#f0f0f0' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Member</TableCell>
                      <TableCell>Reward</TableCell>
                      <TableCell>Earned</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" width={200} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(awards?.rows ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6, color: colors.textSecondary }}>
                          {awardStatus === 'earned' ? 'All caught up — nothing to hand over.' : 'Nothing here.'}
                        </TableCell>
                      </TableRow>
                    ) : awards!.rows.map((a: any) => {
                      const busy = busyId === a._id;
                      return (
                        <TableRow key={a._id} hover>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar sx={{ width: 34, height: 34, fontSize: 13, bgcolor: '#fdeff5', color: colors.primaryDark }}>
                                {initials(a.user?.name)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{a.user?.name ?? '—'}</Typography>
                                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                  {a.user?.phoneNumber ?? ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{a.rewardText || '—'}</Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {a.visits} {a.visits === 1 ? 'visit' : 'visits'}{a.title ? ` · ${a.title}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: colors.textSecondary }}>{shortDate(a.createdAt)}</TableCell>
                          <TableCell>
                            {a.status === 'earned' && (
                              <Typography variant="body2" sx={{ color: colors.textSecondary }}>Waiting</Typography>
                            )}
                            {a.status === 'fulfilled' && (
                              <Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Check sx={{ fontSize: 16, color: '#2e7d32' }} />
                                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2e7d32' }}>
                                    Handed over
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                  {shortDate(a.fulfilledAt)}{a.fulfilledBy?.name ? ` · by ${a.fulfilledBy.name}` : ''}
                                </Typography>
                              </Box>
                            )}
                            {a.status === 'revoked' && (
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ color: colors.textSecondary }}>
                                  Revoked
                                </Typography>
                                {a.revokedReason && (
                                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                    {a.revokedReason}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                              {a.status === 'earned' && (
                                <Button
                                  size="small" variant="contained" disableElevation
                                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Check />}
                                  disabled={busy}
                                  onClick={() => fulfil(a)}
                                  sx={{ borderRadius: 999, px: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                  Hand over
                                </Button>
                              )}
                              {a.status !== 'revoked' && (
                                <Tooltip title="Revoke">
                                  <span>
                                    <IconButton
                                      size="small" disabled={busy} aria-label="Revoke"
                                      onClick={() => openRevoke(a)}
                                      sx={{ color: colors.textSecondary, '&:hover': { color: '#d32f2f' } }}
                                    >
                                      <RemoveCircleOutline fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {(awards?.pages ?? 0) > 1 && (
                <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
                  <Button size="small" disabled={awardPage <= 1} onClick={() => setAwardPage((p) => p - 1)}>Prev</Button>
                  <Typography variant="body2" sx={{ alignSelf: 'center' }}>{awardPage} / {awards!.pages}</Typography>
                  <Button size="small" disabled={awardPage >= awards!.pages} onClick={() => setAwardPage((p) => p + 1)}>Next</Button>
                </Box>
              )}
            </SectionCard>
          )}

          {/* Revoke — a deliberate step with a recorded reason, never a browser prompt. */}
          <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Revoke reward?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                {revokeTarget?.user?.name ?? 'This member'} · {revokeTarget?.rewardText} ({revokeTarget?.visits} visits).
                It will disappear from their app.
              </Typography>
              <Typography variant="caption" fontWeight={600} sx={{ color: colors.textSecondary }}>
                REASON
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {[...REVOKE_REASONS, 'Other'].map((reason) => (
                  <Chip
                    key={reason} label={reason}
                    onClick={() => setRevokeReason(reason)}
                    color={revokeReason === reason ? 'primary' : 'default'}
                    variant={revokeReason === reason ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
              {revokeReason === 'Other' && (
                <TextField
                  autoFocus fullWidth size="small" placeholder="Reason" sx={{ mt: 2 }}
                  value={revokeOther} onChange={(e) => setRevokeOther(e.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setRevokeTarget(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained" color="error" disableElevation
                disabled={!finalReason || busyId === revokeTarget?._id}
                onClick={confirmRevoke}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999, px: 2.5 }}
              >
                Revoke
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default MilestonesPage;
